<template>
  <v-container>
    <v-row>
      <!-- Header -->
      <v-col cols="12" class="pb-1">
        <div class="d-flex align-center" style="gap: 10px;">
          <v-icon color="warning">mdi-alert-circle-outline</v-icon>
          <span class="text-h6 font-weight-medium">Alertas contables PSP</span>
          <v-chip
            v-if="!loading && totalAlertas > 0"
            small
            color="warning"
            text-color="white"
            class="font-weight-bold"
          >
            {{ totalAlertas }}
          </v-chip>
        </div>
        <div class="caption grey--text mt-1 ml-8">
          Productos con precio por sucursal sin configuración contable completa
        </div>
      </v-col>

      <!-- Loading -->
      <v-col v-if="loading" cols="12">
        <v-skeleton-loader type="list-item-two-line" v-for="i in 5" :key="i" class="mb-1" />
      </v-col>

      <template v-else>
        <!-- Banner estado sucursal contable -->
        <v-col cols="12" class="py-1">
          <v-sheet
            rounded="lg"
            class="pa-3 d-flex align-center"
            :color="sucursalBannerColor"
            style="gap: 10px;"
          >
            <v-icon :color="sucursalBannerIconColor" small>{{ sucursalBannerIcon }}</v-icon>
            <span class="caption font-weight-medium" :class="sucursalBannerTextClass">
              {{ sucursalBannerText }}
            </span>
          </v-sheet>
        </v-col>

        <!-- Sin alertas -->
        <v-col v-if="totalAlertas === 0" cols="12">
          <v-sheet rounded="lg" class="pa-6 d-flex flex-column align-center text-center" color="transparent" style="border: 1px dashed rgba(255,255,255,0.15);">
            <v-icon color="success" large class="mb-2">mdi-check-circle-outline</v-icon>
            <div class="subtitle-2">Todo en orden</div>
            <div class="caption grey--text">Todos los PSP tienen configuración contable completa.</div>
          </v-sheet>
        </v-col>

        <!-- Lista de alertas -->
        <v-col v-else cols="12" class="pt-1">
          <v-list class="pa-0" style="background: transparent;">
            <v-list-item
              v-for="alerta in alertas"
              :key="alerta.psp_id"
              class="alerta-item mb-1 px-3"
              @click="irAProducto(alerta.psp_id)"
            >
              <!-- Indicador de severidad -->
              <v-list-item-avatar size="32" class="mr-3" style="min-width: 32px;">
                <v-icon
                  :color="alerta.sin_contabilidad ? 'deep-orange' : 'orange'"
                  small
                >
                  {{ alerta.sin_contabilidad ? 'mdi-alert-circle' : 'mdi-alert' }}
                </v-icon>
              </v-list-item-avatar>

              <v-list-item-content class="py-2">
                <v-list-item-title class="body-2 font-weight-medium">
                  {{ alerta.presentacion }}
                </v-list-item-title>
                <v-list-item-subtitle class="mt-1" style="white-space: normal;">
                  <v-chip
                    v-if="alerta.sin_contabilidad"
                    x-small
                    color="deep-orange darken-1"
                    text-color="white"
                    class="mr-1"
                    label
                  >
                    Sin contabilidad
                  </v-chip>
                  <v-chip
                    v-if="alerta.sin_rubro_venta"
                    x-small
                    color="orange darken-1"
                    text-color="white"
                    class="mr-1"
                    label
                  >
                    Sin rubro venta
                  </v-chip>
                  <v-chip
                    v-if="alerta.sin_rubro_compra"
                    x-small
                    color="orange darken-1"
                    text-color="white"
                    label
                  >
                    Sin rubro compra
                  </v-chip>
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action>
                <v-icon small color="grey">mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-col>
      </template>
    </v-row>
  </v-container>
</template>

<script>
import ContabilidadApi from "@/api/contabilidad";

export default {
  name: "SucursalContabilidadAlertasPSP",
  data() {
    return {
      loading: false,
      totalAlertas: 0,
      alertas: [],
      sucursalContable: {
        existe: false,
        tiene_rubro_venta: false,
        tiene_rubro_compra: false,
      },
    };
  },
  computed: {
    sucursalUuid() {
      return this.$route.params.uuid_sucursal || null;
    },
    sucursalBannerColor() {
      if (!this.sucursalContable.existe) return "rgba(244,67,54,0.08)";
      if (!this.sucursalContable.tiene_rubro_venta || !this.sucursalContable.tiene_rubro_compra)
        return "rgba(255,152,0,0.08)";
      return "rgba(76,175,80,0.08)";
    },
    sucursalBannerIcon() {
      if (!this.sucursalContable.existe) return "mdi-close-circle-outline";
      if (!this.sucursalContable.tiene_rubro_venta || !this.sucursalContable.tiene_rubro_compra)
        return "mdi-alert-outline";
      return "mdi-check-circle-outline";
    },
    sucursalBannerIconColor() {
      if (!this.sucursalContable.existe) return "error";
      if (!this.sucursalContable.tiene_rubro_venta || !this.sucursalContable.tiene_rubro_compra)
        return "warning";
      return "success";
    },
    sucursalBannerTextClass() {
      if (!this.sucursalContable.existe) return "red--text text--lighten-1";
      if (!this.sucursalContable.tiene_rubro_venta || !this.sucursalContable.tiene_rubro_compra)
        return "orange--text text--lighten-1";
      return "green--text text--lighten-1";
    },
    sucursalBannerText() {
      if (!this.sucursalContable.existe)
        return "La sucursal no tiene configuración contable. Los PSP sin configurar no tienen fallback.";
      const faltantes = [
        !this.sucursalContable.tiene_rubro_venta && "rubro de venta",
        !this.sucursalContable.tiene_rubro_compra && "rubro de compra",
      ].filter(Boolean);
      if (faltantes.length)
        return `Fallback de sucursal incompleto — falta: ${faltantes.join(" y ")}.`;
      return "Configuración contable de la sucursal completa. Los PSP sin configurar usarán este fallback.";
    },
  },
  methods: {
    irAProducto(pspId) {
      this.$router.push({
        name: "empresa.sucursal.productos.item",
        params: {
          uuid_sucursal: this.sucursalUuid,
          id_producto_sucursal: pspId,
        },
      });
    },
    async fetchAlertas() {
      this.loading = true;
      try {
        const { data } = await ContabilidadApi.getAlertasPSP(this.sucursalUuid);
        this.sucursalContable = data.sucursal_contable;
        this.totalAlertas = data.total_alertas;
        this.alertas = data.alertas;
      } catch {
        this.$updateBus.$emit("showSnackBar", {
          show: true,
          button: false,
          color: "error",
          text: "Error al obtener las alertas contables",
          icon: "mdi-alert-circle",
          timeout: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
  },
  created() {
    this.fetchAlertas();
  },
};
</script>

<style scoped>
.alerta-item {
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 10px;
  transition: background 0.15s ease, border-color 0.15s ease;
  cursor: pointer;
}
.alerta-item:hover {
  background: rgba(255, 255, 255, 0.04) !important;
  border-color: rgba(255, 255, 255, 0.15);
}
</style>
